import React, { useState, useMemo } from 'react';
import { sortBy } from '../../../utils/sortBy';
import { format } from 'date-fns';
import { LineChart, XAxis, YAxis, Line } from 'recharts';
import { usePostsChartData } from '../../../api/hooks/usePostsChartData';
import { DateRangeFilter } from '../DateRangeFilter';
import { Switch } from '../../../components/ui/switch';
import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../components/ui/chart';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { useTranslate } from 'ra-core';
import { CHART_TICK_FONT_SIZE } from '../chartStyles';

interface ChartData {
  date: string;
  posts: number;
  reactions: number;
  comments: number;
}

interface FeedChartProps {
  after: string;
  before: string;
  setAfter: (date: string) => void;
  setBefore: (date: string) => void;
}

export const FeedChart: React.FC<FeedChartProps> = ({
  after,
  before,
  setAfter,
  setBefore,
}) => {
  const translate = useTranslate();
  const [showPosts, setShowPosts] = useState(true);
  const [showReactions, setShowReactions] = useState(true);
  const [showComments, setShowComments] = useState(true);

  const { data: chartData } = usePostsChartData({
    after: new Date(after).toISOString(),
    before: new Date(before).toISOString(),
  });

  const sortedData = useMemo(() => {
    if (!chartData) return [];
    const dateValuesLookup: Record<string, ChartData> = {};

    const getDateValue = (timestamp: string) => {
      const isoDate = format(new Date(timestamp), 'yyyy-MM-dd');

      const value = (dateValuesLookup[isoDate] ??= {
        date: timestamp,
        posts: 0,
        reactions: 0,
        comments: 0,
      });
      return value;
    };

    chartData.Posts.forEach((p) => getDateValue(p.createdOn).posts++);
    chartData.Reactions.forEach((r) => getDateValue(r.createdOn).reactions++);
    chartData.Comments.forEach((c) => getDateValue(c.createdOn).comments++);

    const dateValues = Object.values(dateValuesLookup);
    return sortBy(dateValues, (x) => x.date);
  }, [chartData]);

  const postsLabel = translate('moonstar.dashboard.feed.posts');
  const commentsLabel = translate('moonstar.dashboard.feed.comments');
  const reactionsLabel = translate('moonstar.dashboard.feed.reactions');

  const chartConfig = {
    posts: { label: postsLabel, color: 'var(--chart-1)' },
    comments: { label: commentsLabel, color: 'var(--chart-4)' },
    reactions: { label: reactionsLabel, color: 'var(--chart-5)' },
  } satisfies ChartConfig;

  return (
    <Card className="shadow-md">
      <CardHeader className="flex flex-wrap items-center justify-between gap-4 pb-2">
        <CardTitle className="text-base font-bold">
          {translate('moonstar.dashboard.feed.title')}
        </CardTitle>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <ChartToggle
              label={postsLabel}
              checked={showPosts}
              onCheckedChange={setShowPosts}
            />
            <ChartToggle
              label={commentsLabel}
              checked={showComments}
              onCheckedChange={setShowComments}
            />
            <ChartToggle
              label={reactionsLabel}
              checked={showReactions}
              onCheckedChange={setShowReactions}
            />
          </div>
          <DateRangeFilter
            startDate={after}
            endDate={before}
            setStartDate={setAfter}
            setEndDate={setBefore}
          />
        </div>
      </CardHeader>

      <CardContent className="p-2 pt-0">
        <ChartContainer
          config={chartConfig}
          className="h-[350px] w-full aspect-auto"
        >
          <LineChart
            data={sortedData}
            margin={{ top: 5, right: 20, left: 10, bottom: 50 }}
          >
            <XAxis
              dataKey="date"
              tickFormatter={(date) => format(new Date(date), 'EEE dd/MM')}
              tick={{ fontSize: CHART_TICK_FONT_SIZE }}
              strokeWidth={0.5}
              angle={-45}
              textAnchor="end"
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              stroke="var(--color-posts)"
              tickFormatter={(value) => Math.round(value).toString()}
              tick={{ fontSize: CHART_TICK_FONT_SIZE }}
              strokeWidth={0.5}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={(value) => Math.round(value).toString()}
              tick={{ fontSize: CHART_TICK_FONT_SIZE }}
              stroke="var(--color-reactions)"
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(date) =>
                    format(new Date(date), 'EEE dd/MM/yyyy')
                  }
                />
              }
            />
            <ChartLegend verticalAlign="bottom" iconType="plainline" />
            <Line
              yAxisId="left"
              type="linear"
              dataKey="posts"
              name={postsLabel}
              stroke="var(--color-posts)"
              strokeWidth={2}
              dot={false}
              hide={!showPosts}
            />

            <Line
              yAxisId="left"
              type="linear"
              dataKey="comments"
              name={commentsLabel}
              stroke="var(--color-comments)"
              strokeWidth={2}
              dot={false}
              hide={!showComments}
            />

            <Line
              yAxisId="right"
              type="linear"
              dataKey="reactions"
              name={reactionsLabel}
              stroke="var(--color-reactions)"
              strokeWidth={2}
              dot={false}
              hide={!showReactions}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

function ChartToggle({
  label,
  checked,
  onCheckedChange,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <Label className="flex items-center gap-2 text-sm font-normal">
      <Switch checked={checked} onCheckedChange={onCheckedChange} size="sm" />
      {label}
    </Label>
  );
}
